import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  AppBar,
  Toolbar,
  IconButton,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import {
  ArrowBack,
  Favorite,
  Add,
  Remove,
  ShoppingCart,
  CardGiftcard,
  CheckCircle,
  AccessTime,
  TrendingUp,
  EmojiEvents,
  Edit as EditIcon,
  CreditCard,
  Mail,
  Star,
  Replay,
  Undo,
} from '@mui/icons-material';
import { useHeart } from '../contexts/HeartContext';
import { parseKSTDate } from '../utils/dateUtils';
import { apiClient } from '../config/api';
import { HeartTransaction, RewardPolicyDto } from '../types';

type TransactionType = 'ALL' | 'CONSUME' | 'PURCHASE' | 'REWARD';

const WalletDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { balance, refreshBalance } = useHeart();
  const [activeTab, setActiveTab] = useState<TransactionType>('ALL');
  const [transactions, setTransactions] = useState<HeartTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  // 리워드 정책 상태
  const [rewards, setRewards] = useState<RewardPolicyDto[]>([]);
  const [rewardsLoading, setRewardsLoading] = useState(true);
  const [rewardsError, setRewardsError] = useState<string | null>(null);

  // 리워드 정책 조회
  const fetchRewards = useCallback(async () => {
    try {
      setRewardsLoading(true);
      setRewardsError(null);
      const policies = await apiClient.getRewardPolicies();
      setRewards(policies);
    } catch (err: any) {
      setRewardsError(err.message || '리워드 정보를 불러올 수 없습니다');
    } finally {
      setRewardsLoading(false);
    }
  }, []);

  // 거래 내역 조회
  const fetchTransactions = useCallback(async (pageNum: number = 0, type?: TransactionType) => {
    try {
      setLoading(true);
      setError(null);

      const filterType = type === 'ALL' ? undefined : type;
      const response = await apiClient.getHeartTransactions({
        page: pageNum,
        size: 20,
        type: filterType,
      });

      if (pageNum === 0) {
        setTransactions(response.transactions);
      } else {
        setTransactions(prev => [...prev, ...response.transactions]);
      }

      setHasMore(response.hasNext);
    } catch (err: any) {
      setError(err.message || '거래 내역을 불러올 수 없습니다');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRewards();
  }, [fetchRewards]);

  useEffect(() => {
    fetchTransactions(0, activeTab);
  }, [activeTab, fetchTransactions]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: TransactionType) => {
    setActiveTab(newValue);
    setPage(0);
  };

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchTransactions(nextPage, activeTab);
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'CONSUME_CUPID_CARD_OPEN':
      case 'CONSUME_CUPID_CARD_EXTEND':
        return <CreditCard sx={{ color: 'error.main' }} />;
      case 'CONSUME_MESSAGE':
        return <Mail sx={{ color: 'error.main' }} />;
      case 'CONSUME_SUPER_LIKE':
        return <Star sx={{ color: 'warning.main' }} />;
      case 'CONSUME_PROFILE_BOOST':
        return <TrendingUp sx={{ color: 'error.main' }} />;
      case 'CONSUME_GIFT':
        return <CardGiftcard sx={{ color: 'error.main' }} />;
      case 'CONSUME_REWIND':
        return <Replay sx={{ color: 'error.main' }} />;
      case 'CONSUME_UNLIMITED_LIKE':
        return <Favorite sx={{ color: 'error.main' }} />;
      case 'PURCHASE':
        return <ShoppingCart sx={{ color: 'primary.main' }} />;
      case 'EARN_DAILY_FREE':
        return <CheckCircle sx={{ color: 'success.main' }} />;
      case 'EARN_REWARD':
        return <EmojiEvents sx={{ color: 'success.main' }} />;
      case 'REFUND':
        return <Undo sx={{ color: 'info.main' }} />;
      case 'ADMIN_ADJUSTMENT':
        return <EditIcon sx={{ color: 'info.main' }} />;
      default:
        if (type.startsWith('CONSUME')) return <Remove sx={{ color: 'error.main' }} />;
        if (type.startsWith('EARN')) return <CardGiftcard sx={{ color: 'success.main' }} />;
        return <Favorite sx={{ color: 'grey.500' }} />;
    }
  };

  const getTransactionColor = (type: string) => {
    switch (type) {
      case 'REFUND':
      case 'ADMIN_ADJUSTMENT':
        return 'info.main';
      default:
        if (type.startsWith('CONSUME')) return 'error.main';
        if (type.startsWith('EARN') || type === 'PURCHASE') return 'success.main';
        return 'text.primary';
    }
  };

  const formatDate = (dateString: string) => {
    const date = parseKSTDate(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return '방금 전';
    if (diffMins < 60) return `${diffMins}분 전`;
    if (diffHours < 24) return `${diffHours}시간 전`;
    if (diffDays < 7) return `${diffDays}일 전`;

    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'Asia/Seoul',
    });
  };

  const getRewardIcon = (rewardType: string) => {
    switch (rewardType) {
      case 'SIGNUP':
        return <CardGiftcard sx={{ color: 'primary.main', fontSize: 32 }} />;
      case 'PROFILE_COMPLETE':
        return <AccessTime sx={{ color: 'success.main', fontSize: 32 }} />;
      case 'DAILY_CHECKIN':
        return <CheckCircle sx={{ color: 'success.main', fontSize: 32 }} />;
      case 'PHOTO_UPLOAD':
        return <Star sx={{ color: 'secondary.main', fontSize: 32 }} />;
      case 'BIO_COMPLETE':
        return <EditIcon sx={{ color: 'info.main', fontSize: 32 }} />;
      case 'REFERRAL':
        return <Star sx={{ color: 'primary.main', fontSize: 32 }} />;
      case 'STREAK_7DAYS':
        return <EmojiEvents sx={{ color: 'warning.main', fontSize: 32 }} />;
      case 'FIRST_MATCH':
        return <Favorite sx={{ color: 'error.main', fontSize: 32 }} />;
      default:
        return <CardGiftcard sx={{ color: 'grey.500', fontSize: 32 }} />;
    }
  };

  const getRewardChipColor = (rewardType: string): "primary" | "success" | "secondary" | "info" | "warning" | "error" | "default" => {
    switch (rewardType) {
      case 'SIGNUP':
        return 'primary';
      case 'PROFILE_COMPLETE':
      case 'DAILY_CHECKIN':
        return 'success';
      case 'PHOTO_UPLOAD':
        return 'secondary';
      case 'BIO_COMPLETE':
        return 'info';
      case 'STREAK_7DAYS':
        return 'warning';
      case 'FIRST_MATCH':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* 헤더 */}
      <AppBar position="static" elevation={0}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate(-1)}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700 }}>
            내 지갑
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 3 }}>
        {/* 현재 잔액 */}
        <Paper sx={{ p: 3, mb: 3, textAlign: 'center', bgcolor: 'rgba(13,31,60,0.06)' }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            현재 보유 하트
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
            <Favorite sx={{ fontSize: 40, color: 'primary.main' }} />
            <Typography variant="h3" fontWeight={700} color="primary.main">
              {balance}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<ShoppingCart />}
            onClick={() => navigate('/heart/purchase')}
            sx={{ minWidth: 200 }}
          >
            하트 충전하기
          </Button>
        </Paper>

        {/* 무료 하트 획득 섹션 */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" fontWeight={700}>
              🎁 무료로 하트 받기
            </Typography>
            <Chip label="가입 혜택" color="primary" size="small" />
          </Box>

          {rewardsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : rewardsError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {rewardsError}
            </Alert>
          ) : rewards.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                현재 제공 중인 리워드가 없습니다
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {rewards.map((reward) => (
                <Box
                  key={reward.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2,
                    bgcolor: 'grey.50',
                    borderRadius: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {getRewardIcon(reward.rewardType)}
                    <Box>
                      <Typography variant="body1" fontWeight={600}>
                        {reward.rewardTypeTitle}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {reward.description || `${reward.periodTitle} (${reward.maxCount ? `최대 ${reward.maxCount}회` : '1회'})`}
                      </Typography>
                    </Box>
                  </Box>
                  <Chip label={`+${reward.amount} ❤️`} color={getRewardChipColor(reward.rewardType)} />
                </Box>
              ))}
            </Box>
          )}
        </Paper>

        {/* 거래 내역 */}
        <Paper sx={{ mb: 3 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth">
              <Tab label="전체" value="ALL" />
              <Tab label="구매" value="PURCHASE" />
              <Tab label="획득" value="REWARD" />
              <Tab label="소비" value="CONSUME" />
            </Tabs>
          </Box>

          {loading && transactions.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ m: 2 }}>
              {error}
            </Alert>
          ) : transactions.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                거래 내역이 없습니다
              </Typography>
            </Box>
          ) : (
            <List>
              {transactions.map((tx, index) => (
                <React.Fragment key={tx.id}>
                  {index > 0 && <Divider component="li" />}
                  <ListItem>
                    <Box sx={{ mr: 2 }}>{getTransactionIcon(tx.transactionType)}</Box>
                    <ListItemText
                      primary={tx.transactionTypeTitle || tx.reason || tx.transactionType}
                      secondary={formatDate(tx.createdTime)}
                      primaryTypographyProps={{ fontWeight: 600 }}
                    />
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography
                        variant="body1"
                        fontWeight={700}
                        color={getTransactionColor(tx.transactionType)}
                      >
                        {tx.transactionType.startsWith('CONSUME') ? '-' : '+'}
                        {Math.abs(tx.amount)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        잔액 {tx.balanceAfter}
                      </Typography>
                    </Box>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}

          {hasMore && !loading && transactions.length > 0 && (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Button onClick={handleLoadMore} disabled={loading}>
                더 보기
              </Button>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default WalletDetailPage;
